import Avatar from '@components/avatar';
import { showNotification } from '@helpers/notification-helper';
import { useAppDispatch, useAppSelector } from '@hooks/app/redux';
import { useInitialValues } from '@pages/kyc/components/kyc-profile/kyc-profile.schema';
import { updateAddresses, updateProfile } from '@services/external/user.api';
import { actions } from '@store/ducks/users';
import {
  Button,
  Col,
  CountrySelect,
  DateField,
  Form,
  MessageBox,
  Row,
  Slider,
  TextField,
} from '@ui';
import moment from 'moment';
import React from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import AvatarUploader from './components/avatar-uploader';
import { PersonalProfileSC } from './personal-profile.styled';

const gve = (e: any) => e;

const PersonalProfile: React.FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const user = useAppSelector((state) => state.users.currentUser)!;
  const initialValues = useInitialValues();

  const [, formOptions] = Form.useForm({
    initialValues,
    onSubmit: async (v) => {
      try {
        const permanentAddress = user.addresses.find(
          (address: any) => address.type === 'permanent',
        );
        const actualAddress = user.addresses.find((address: any) => address.type === 'actual');

        const addresses = [
          {
            ...permanentAddress,
            city: v.city,
            country: v.country,
            postCode: v.postalCode,
            address: v.address,
            type: 'permanent',
          },
        ];

        if (actualAddress) {
          addresses.push(actualAddress);
        }

        await Promise.all([
          updateProfile({ ...v, birthDate: moment(v.birthDate).format('YYYY-MM-DD') }),
          updateAddresses(addresses),
        ]);

        showNotification({
          type: 'SUCCESS',
          title: formatMessage({ id: 'profile_edit.success_alert.title' }),
          copy: '',
        });
        dispatch(actions.getCurrentUserRequest());
      } catch (error: any) {
        console.log('error', error);

        const errCode = error.data?.data?.errCode;
        const message = errCode ? `server_error.${errCode}` : 'server_error.something_wrong';

        showNotification({
          type: 'ERROR',
          title: formatMessage({ id: 'profie_edit.error_alert.title' }),
          copy: formatMessage({ id: message }),
        });
      }
    },
  });

  return (
    <PersonalProfileSC>
      <Form {...formOptions} disabled>
        <AvatarUploader />
        <MessageBox
          variant="warning"
          style={{ marginBottom: theme.spacings.text.S }}
          title="Niektoré údaje nie je možné meniť"
          description={'Vaša identita je overená, a preto je nie je možné meniť niektoré údaje'}
        />
        <Form.Section label={formatMessage({ id: 'kyc.personalLabel' })}>
          <Row gutter={[24, 0]}>
            <Col xs={12} md={6}>
              <Form.Field
                name="firstName"
                label={formatMessage({ id: 'kyc.firstNameLabel' })}
                required
              >
                <TextField autoComplete="given-name" />
              </Form.Field>
            </Col>
            <Col xs={12} md={6}>
              <Form.Field
                name="lastName"
                label={formatMessage({ id: 'kyc.lastNameLabel' })}
                required
              >
                <TextField autoComplete="family-name" />
              </Form.Field>
            </Col>
          </Row>
          <Row className="date-row">
            <Col xs={12} sm={12} md={12} xl={8}>
              <Form.Field
                name="birthDate"
                label={formatMessage({ id: 'kyc.birthDateLabel' })}
                required
                getValueFromEvent={(e) => e}
              >
                <DateField />
              </Form.Field>
            </Col>
          </Row>
          <Form.Field
            name="pin"
            label={formatMessage({ id: 'kyc.pinLabel' })}
            required
            caption={formatMessage({ id: 'kyc.pinCaption' })}
          >
            <TextField />
          </Form.Field>
        </Form.Section>
        <Form.Section label={formatMessage({ id: 'kyc.addressSectionLabel' })}>
          <Form.Field name="address" label={formatMessage({ id: 'kyc.addressLabel' })} required>
            <TextField autoComplete="street-address" />
          </Form.Field>
          <Row gutter={[24, 0]}>
            <Col xs={12} md={6}>
              <Form.Field name="city" label={formatMessage({ id: 'kyc.cityLabel' })} required>
                <TextField autoComplete="address-line1" />
              </Form.Field>
            </Col>
            <Col xs={12} md={6}>
              <Form.Field
                name="postalCode"
                label={formatMessage({ id: 'kyc.postalLabel' })}
                required
              >
                <TextField autoComplete="postal-code" />
              </Form.Field>
            </Col>
          </Row>
          <Form.Field
            name="country"
            label={formatMessage({ id: 'kyc.countyLabel' })}
            required
            getValueFromEvent={gve}
          >
            <CountrySelect useAuthCountries />
          </Form.Field>
        </Form.Section>
        <Form.Custom>
          {(f) => (
            <Row
              justify="end"
              style={{ marginTop: theme.spacings.component.L }}
              gutter={theme.spacings.component.S}
            >
              <Col xs={6}>
                <Button
                  size="large"
                  type="submit"
                  variant="primary"
                  onClick={() => {
                    f.submitForm();
                  }}
                  loading={f.isSubmitting}
                  disabled
                >
                  Uložiť
                </Button>
              </Col>
            </Row>
          )}
        </Form.Custom>
      </Form>
    </PersonalProfileSC>
  );
};

export default PersonalProfile;
